import { useEffect, useState, useCallback } from "react";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Heart, Rocket, ChevronDown, ChevronUp, MessageSquarePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import FeedbackDialog from "../components/FeedbackDialog";

const STATUS_META = {
  evaluating: { label: "In valutazione", cls: "bg-[#F0EAE1] text-[#8C6D4F]" },
  planned: { label: "In programma", cls: "bg-[#E4EFE8] text-[#1B3B2B]" },
  in_development: { label: "In sviluppo", cls: "bg-[#E3ECF7] text-[#274C77]" },
  available: { label: "Disponibile", cls: "bg-[#EAF1DC] text-[#4B5F1E]" },
};

function RoadmapCard({ item, onInterest, onSuggest }) {
  const [expanded, setExpanded] = useState(false);
  const meta = STATUS_META[item.status] || STATUS_META.evaluating;
  return (
    <div data-testid={`roadmap-card-${item.id}`} className="bg-white rounded-2xl border border-[#E5DEC9] p-5 eq-fade-up">
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-[#1B3B2B] min-w-0">{item.title}</h2>
        <span data-testid={`roadmap-status-${item.id}`} className={`shrink-0 text-[11px] font-mono-label rounded-full px-2.5 py-1 ${meta.cls}`}>{meta.label}</span>
      </div>
      <p className="text-sm text-[#5F6B64] mt-2 leading-relaxed">{item.short_description}</p>
      {item.status === "evaluating" && (
        <p className="text-xs text-[#8C6D4F] mt-2 italic">Stiamo valutando questa idea: non è una promessa.</p>
      )}
      {item.long_description && (
        <div className="mt-3">
          {expanded && <p className="text-sm text-[#3F4A44] leading-relaxed mb-2">{item.long_description}</p>}
          <button type="button" data-testid={`roadmap-expand-${item.id}`} onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#8C532B] hover:text-[#5A3419]">
            {expanded ? <>Mostra meno <ChevronUp className="h-3.5 w-3.5" /></> : <>Scopri di più <ChevronDown className="h-3.5 w-3.5" /></>}
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button size="sm" variant={item.interested ? "default" : "outline"} data-testid={`roadmap-interest-${item.id}`}
          onClick={() => onInterest(item)}
          className={item.interested ? "bg-[#1B3B2B] hover:bg-[#142E1F]" : "border-[#1B3B2B] text-[#1B3B2B] hover:bg-[#F1F4F0]"}>
          <Heart className={`h-4 w-4 mr-1.5 ${item.interested ? "fill-current" : ""}`} /> {item.interested ? "Ti interessa" : "Mi interessa"}
        </Button>
        <Button size="sm" variant="ghost" data-testid={`roadmap-suggest-${item.id}`} onClick={() => onSuggest(item)}
          className="text-[#8C532B] hover:bg-[#F0EAE1]">
          <MessageSquarePlus className="h-4 w-4 mr-1.5" /> Lascia un suggerimento
        </Button>
      </div>
    </div>
  );
}

export default function InArrivo() {
  const [items, setItems] = useState(null);
  const [feedbackItem, setFeedbackItem] = useState(null);
  const [fbOpen, setFbOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/roadmap").then((r) => setItems(r.data)).catch((e) => { setItems([]); toast.error(errText(e.response?.data?.detail)); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleInterest = async (item) => {
    const want = !item.interested;
    setItems((arr) => arr.map((i) => (i.id === item.id ? { ...i, interested: want } : i)));
    try {
      if (want) await api.post(`/roadmap/${item.id}/interest`);
      else await api.delete(`/roadmap/${item.id}/interest`);
    } catch (e) {
      setItems((arr) => arr.map((i) => (i.id === item.id ? { ...i, interested: !want } : i)));
      toast.error(errText(e.response?.data?.detail));
    }
  };

  const suggest = (item) => { setFeedbackItem({ id: item.id, title: item.title }); setFbOpen(true); };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="roadmap-page">
      <div className="eq-fade-up">
        <p className="font-mono-label text-[#8C532B] text-xs flex items-center gap-1.5"><Rocket className="h-3.5 w-3.5" /> Direzione prodotto</p>
        <h1 className="font-display text-2xl lg:text-3xl font-bold text-[#1B3B2B]">In arrivo</h1>
        <p className="text-sm text-[#5F6B64] mt-1">Le principali aree su cui Equiro sta lavorando o valutando. Le date non sono impegni: usa “Mi interessa” per aiutarci a dare priorità.</p>
      </div>
      {items === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1B3B2B]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E5DEC9] py-16 text-center text-sm text-[#5F6B64]" data-testid="roadmap-empty">
          Al momento non ci sono elementi pubblicati. Torna a trovarci presto.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => <RoadmapCard key={it.id} item={it} onInterest={toggleInterest} onSuggest={suggest} />)}
        </div>
      )}
      <FeedbackDialog open={fbOpen} onOpenChange={setFbOpen} roadmapItem={feedbackItem} defaultType="suggestion" />
    </div>
  );
}
